import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';

const ThemeContext = createContext({});

// Convert hex color to RGB values for CSS variables
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
    : null;
};

// Calculate contrast color (black or white) based on background
const getContrastColor = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '255 255 255';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '28 28 30' : '255 255 255';
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState({
    primary_color: '#D4A574',
    secondary_color: '#78716C',
    accent_color: '#292524',
    // iter-386u-bo — Branding & UI defaults
    sidebar_color: '#0f172a',
    page_bg_color: '#f8fafc',
    card_bg_color: '#ffffff',
    button_color: '#059669',
    organization_name: '',
    logo_url: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchThemeSettings();
  }, []);

  const fetchThemeSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/organization/settings`);
      const settings = response.data;
      console.log('ThemeContext: fetched settings', settings);
      
      if (settings) {
        setTheme(prev => ({
          ...prev,
          primary_color: settings.primary_color || '#D4A574',
          secondary_color: settings.secondary_color || '#78716C',
          accent_color: settings.accent_color || '#292524',
          sidebar_color: settings.sidebar_color || '#0f172a',
          page_bg_color: settings.page_bg_color || '#f8fafc',
          card_bg_color: settings.card_bg_color || '#ffffff',
          button_color: settings.button_color || '#059669',
          organization_name: settings.organization_name || '',
          logo_url: settings.logo_url || null,
        }));

        // Apply colors to CSS variables
        applyThemeToDOM(settings);
      }
    } catch (error) {
      console.error('Failed to fetch theme settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (settings) => {
    const root = document.documentElement;
    
    ['primary_color', 'secondary_color', 'accent_color'].forEach((k) => {
      if (/^#[0-9a-f]{6}$/i.test(settings[k] || '')) root.style.setProperty(`--brand-${k.split('_')[0]}-hex`, settings[k]);
    });
    // Primary color - used for buttons, accents, highlights
    if (settings.primary_color) {
      const primaryRgb = hexToRgb(settings.primary_color);
      const primaryForeground = getContrastColor(settings.primary_color);
      if (primaryRgb) {
        root.style.setProperty('--brand-primary', primaryRgb);
        root.style.setProperty('--brand-primary-foreground', primaryForeground);
        // Also update shadcn primary for buttons
        root.style.setProperty('--primary', primaryRgb);
        root.style.setProperty('--primary-foreground', primaryForeground);
      }
    }

    // Secondary color - used for secondary elements
    if (settings.secondary_color) {
      const secondaryRgb = hexToRgb(settings.secondary_color);
      const secondaryForeground = getContrastColor(settings.secondary_color);
      if (secondaryRgb) {
        root.style.setProperty('--brand-secondary', secondaryRgb);
        root.style.setProperty('--brand-secondary-foreground', secondaryForeground);
      }
    }

    // Accent color - used for text, borders
    if (settings.accent_color) {
      const accentRgb = hexToRgb(settings.accent_color);
      const accentForeground = getContrastColor(settings.accent_color);
      if (accentRgb) {
        root.style.setProperty('--brand-accent', accentRgb);
        root.style.setProperty('--brand-accent-foreground', accentForeground);
        // Update ring color for focus states
        root.style.setProperty('--ring', accentRgb);
      }
    }

    // iter-386u-bo — Branding & UI Engine: 4 dynamic surfaces
    // Each color exposes both a raw hex (for direct style="var(...)") and
    // an RGB triplet (for opacity variants like `bg-[var(--brand-card)/50]`).
    const brandingMap = {
      sidebar_color: 'brand-sidebar',
      page_bg_color: 'brand-page',
      card_bg_color: 'brand-card',
      button_color: 'brand-button',
    };
    Object.entries(brandingMap).forEach(([field, cssVar]) => {
      if (settings[field]) {
        const rgb = hexToRgb(settings[field]);
        const fg = getContrastColor(settings[field]);
        root.style.setProperty(`--${cssVar}`, settings[field]);
        if (rgb) {
          root.style.setProperty(`--${cssVar}-rgb`, rgb);
          root.style.setProperty(`--${cssVar}-fg`, fg);
        }
      }
    });

    // Apply page background directly to body so any missed surfaces still theme
    if (settings.page_bg_color) {
      document.body.style.backgroundColor = settings.page_bg_color;
    }
  };

  const refreshTheme = () => {
    fetchThemeSettings();
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
